import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, Button } from '../components';
import BelagomProfilePhoto from '../components/BelagomProfilePhoto';
import { ComplianceTierMedaille, isMedailleTier, type ComplianceTier, type TierIndicatoren } from '../components/ComplianceTierMedaille';
import api from '../services/api';
import toast from 'react-hot-toast';

/** Kaartrand in de metaalkleur van de tier */
const TIER_RING = {
  GOUD: 'ring-2 ring-[#c9a227]/60',
  ZILVER: 'ring-2 ring-[#9aa3ad]/60',
  BRONS: 'ring-2 ring-[#b06a3b]/50',
} as const;
interface Professional {
  id: string;
  user_id: string;
  first_name: string;
  last_name: string;
  photo_url?: string;
  professional_type?: string;
  // Tarief en jaren zelfstandig horen niet in het HR-profiel (FO)
  average_rating?: number;
  total_reviews?: number;
  availability_status?: string;
  introduction?: string;
  active_regions?: string[];
  languages?: string[];
  /** Profielkop: praktijknaam + logo (FO) */
  company_name?: string | null;
  company_logo_url?: string | null;
  /** Pitch voor de HR-manager (FO stap 3) */
  bio_werkgever?: string | null;
  /** Expertise- en doelgroeptags (FO stap 4) */
  specialisatie_tags?: string[];
  doelgroep_niveaus?: string[];
  match_score?: number | null;
  /** Compliance Vetting Tier: goud > zilver > brons bovenaan (backend-sortering) */
  compliance_tier?: ComplianceTier;
  /** De vier indicatoren van het lint; null zonder medaille */
  compliance_indicatoren?: TierIndicatoren | null;
  /** Reisradius/afstand-annotatie (hemelsbreed KvK ↔ werkgever) */
  reisradius_km?: number | null;
  distance_km?: number | null;
  travel_zone?: number | null;
  travel_forfait_uren?: number | null;
  buiten_reisradius?: boolean;
  reistijd_bijbetaling_eur?: number | null;
}

import { PROFESSIONAL_TYPE_LABELS } from '../types/professionalRates';
import { useProfileTaxonomy, useTaxonomyLabel, type TaxonomyOption } from '../hooks/useProfileTaxonomy';

/** Tagfilter uit de URL: JSON-array of komma-gescheiden lijst */
function parseTagParam(raw: string): string[] {
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed.filter((v): v is string => typeof v === 'string');
  } catch {
    // valt terug op de komma-notatie hieronder
  }
  return raw.split(',').map((v) => v.trim()).filter(Boolean);
}

export const EmployerSearchResultsPage: React.FC = () => {
  const { t } = useTranslation('employer');
  const { t: tDash } = useTranslation('employerDashboard');
  const navigate = useNavigate();
  // Labels voor specialisme- en doelgroeptags komen uit dezelfde taxonomie
  // als het profielformulier en de HR-zoekfilters.
  const { data: taxonomy } = useProfileTaxonomy();
  const label = useTaxonomyLabel();
  const taxonomyLabel = (options: TaxonomyOption[] | undefined, value: string) => {
    const option = options?.find((o) => o.value === value);
    return option ? label(option) : value;
  };
  const [searchParams, setSearchParams] = useSearchParams();
  const [professionals, setProfessionals] = useState<Professional[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [noResults, setNoResults] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const loadMoreRef = React.useRef<HTMLDivElement>(null);

  const SORT_OPTIONS = [
    { value: 'rating_desc', label: t('searchResults.sortBestRating') },
    { value: 'rate_asc', label: t('searchResults.sortLowestRate') },
    { value: 'rate_desc', label: t('searchResults.sortHighestRate') },
    { value: 'availability_asc', label: t('searchResults.sortEarliestAvailable') },
  ];

  const hasMatchScores = professionals.some(p => p.match_score !== null && p.match_score !== undefined);

  useEffect(() => {
    setCurrentPage(1);
    setProfessionals([]);
    setHasMore(true);
    searchProfessionals(1, true);
    // Nieuwe zoekopdracht alleen bij gewijzigde URL-parameters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    if (!loadMoreRef.current || !hasMore || loading || loadingMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore) {
          loadMoreProfessionals();
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(loadMoreRef.current);
    return () => observer.disconnect();
    // Observer wordt na elke lading (loadingMore wisselt) opnieuw gemaakt, dus de closure is vers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMore, loading, loadingMore]);

  const searchProfessionals = async (page: number = 1, reset: boolean = false) => {
    if (reset) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }
    try {
      const params = new URLSearchParams(searchParams);
      params.set('page', page.toString());
      params.set('limit', '50');

      const response = await api.get(`/employer/search-professionals?${params.toString()}`);

      if (response.data.success) {
        const newProfessionals = response.data.data || [];
        if (reset) {
          setProfessionals(newProfessionals);
        } else {
          setProfessionals(prev => [...prev, ...newProfessionals]);
        }
        setNoResults(newProfessionals.length === 0 && page === 1);
        const totalPages = response.data.totalPages || 1;
        setTotalCount(response.data.total || newProfessionals.length);
        setHasMore(page < totalPages);
      }
    } catch (error) {
      console.error('Search error:', error);
      // Stop infinite retry loop on error: prevent more pagination
      setHasMore(false);
      if (reset) {
        setNoResults(true);
        toast.error(t('searchResults.searchError'));
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadMoreProfessionals = () => {
    if (!loadingMore && hasMore) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      searchProfessionals(nextPage, false);
    }
  };

  const handleSortChange = (newSort: string) => {
    const params = new URLSearchParams(searchParams);
    params.set('sort_by', newSort);
    setSearchParams(params);
  };

  const handleContactProfessional = async (professionalId: string) => {
    try {
      const connectionResponse = await api.post('/employer/connection', {
        professional_id: professionalId,
        connection_type: 'project'
      });

      const conversationResponse = await api.post('/communication/conversations/professional-employer', {
        professional_id: professionalId
      });

      if (connectionResponse.data.success && conversationResponse.data.success) {
        toast.success(t('searchResults.connectionCreated'));
        navigate(`/berichten?conversation=${conversationResponse.data.data.id}`);
      }
    } catch (error: any) {
      console.error('Error creating connection/conversation:', error);

      if (error?.response?.status === 400 || error?.response?.status === 409) {
        try {
          const conversationResponse = await api.post('/communication/conversations/professional-employer', {
            professional_id: professionalId
          });
          if (conversationResponse.data.success) {
            navigate(`/berichten?conversation=${conversationResponse.data.data.id}`);
          } else {
            navigate('/berichten');
          }
        } catch {
          navigate('/berichten');
        }
      } else {
        toast.error(t('searchResults.connectionError'));
      }
    }
  };

  const handleRequestSearch = async () => {
    try {
      const criteria: any = {};
      searchParams.forEach((value, key) => {
        try {
          if (value.startsWith('[') || value.startsWith('{')) {
            criteria[key] = JSON.parse(value);
          } else if (value === 'true' || value === 'false') {
            criteria[key] = value === 'true';
          } else if (!isNaN(Number(value)) && value !== '') {
            criteria[key] = Number(value);
          } else {
            criteria[key] = value;
          }
        } catch {
          criteria[key] = value;
        }
      });

      if (!criteria.professional_type) {
        toast.error(t('searchResults.selectTypeFirst'));
        navigate('/employer/dashboard/zoeken/uitgebreid', { state: { criteria } });
        return;
      }

      const response = await api.post('/employer/search-request', {
        professional_type: criteria.professional_type,
        search_criteria: criteria,
        publish_on_community: false,
        active_attitude: criteria.active_attitude || false,
        belagom_search_team_enabled: true
      });

      if (response.data.success) {
        toast.success(t('searchResults.belagomSearchCreated'));
      }
    } catch (error: any) {
      console.error('Error creating search request:', error);
      const errorMessage = error?.response?.data?.message || t('searchResults.belagomSearchError');
      toast.error(errorMessage);

      if (error?.response?.data?.errors?.some((e: string) => e.includes('professional type'))) {
        const criteria: any = {};
        searchParams.forEach((value, key) => {
          try {
            if (value.startsWith('[') || value.startsWith('{')) {
              criteria[key] = JSON.parse(value);
            } else if (value === 'true' || value === 'false') {
              criteria[key] = value === 'true';
            } else if (!isNaN(Number(value)) && value !== '') {
              criteria[key] = Number(value);
            } else {
              criteria[key] = value;
            }
          } catch {
            criteria[key] = value;
          }
        });
        navigate('/employer/dashboard/zoeken/uitgebreid', { state: { criteria } });
      }
    }
  };

  // Build active filter chips from URL params
  const getActiveFilterChips = (): { label: string; key: string }[] => {
    const chips: { label: string; key: string }[] = [];
    const type = searchParams.get('professional_type');
    if (type) chips.push({ label: PROFESSIONAL_TYPE_LABELS[type as keyof typeof PROFESSIONAL_TYPE_LABELS] || type, key: 'professional_type' });

    const regions = searchParams.get('active_regions');
    if (regions) {
      try {
        const parsed: string[] = JSON.parse(regions);
        parsed.forEach(r => chips.push({ label: r, key: 'active_regions' }));
      } catch { /* ignore */ }
    }

    const langs = searchParams.get('languages');
    if (langs) {
      try {
        const parsed: string[] = JSON.parse(langs);
        parsed.forEach(l => chips.push({ label: l, key: 'languages' }));
      } catch { /* ignore */ }
    }

    const avail = searchParams.get('availability_status');
    if (avail && avail !== 'any') {
      chips.push({ label: avail === 'immediately' ? t('searchResults.availableNow') : t('searchResults.specificDate'), key: 'availability_status' });
    }

    // Werkwijze- en tarieffilters zijn vervallen (FO); specialisme en doelgroep
    // zijn nu de sturende filters van het HR-dashboard.
    const tags = searchParams.get('specialisatie_tags');
    if (tags) {
      parseTagParam(tags).forEach((tag) => {
        chips.push({ label: taxonomyLabel(taxonomy?.specialisatie_tags, tag), key: 'specialisatie_tags' });
      });
    }

    const niveaus = searchParams.get('doelgroep_niveaus');
    if (niveaus) {
      parseTagParam(niveaus).forEach((niveau) => {
        chips.push({ label: taxonomyLabel(taxonomy?.doelgroep_niveaus, niveau), key: 'doelgroep_niveaus' });
      });
    }

    const minRating = searchParams.get('min_rating');
    if (minRating) chips.push({ label: `Min. ${minRating}★`, key: 'min_rating' });

    const minTier = searchParams.get('min_tier');
    if (minTier) chips.push({ label: tDash(`zoeken.tierOptie.${minTier}`), key: 'min_tier' });

    return chips;
  };

  const activeFilters = getActiveFilterChips();
  const currentSort = searchParams.get('sort_by') || 'rating_desc';

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card>
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
            <p className="text-gray-600">{t('searchResults.searching')}</p>
          </div>
        </Card>
      </div>
    );
  }

  if (noResults) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <Button variant="outline" onClick={() => navigate('/employer/dashboard/zoeken/uitgebreid')}>
            ← {t('searchResults.backToSearch')}
          </Button>
        </div>

        <Card>
          <div className="text-center py-12">
            <svg className="mx-auto h-16 w-16 text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {t('searchResults.noResults')}
            </h2>
            <p className="text-gray-700 mb-4 max-w-2xl mx-auto">
              {t('searchResults.noResultsDesc')}
            </p>

            <div className="space-y-4 max-w-md mx-auto">
              <Button onClick={handleRequestSearch} className="w-full">
                {t('searchResults.postSearch')}
              </Button>
              <p className="text-sm text-gray-500">
                {t('searchResults.belagomSearchHint')}
              </p>

              <div className="pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => {
                    const criteria: any = {};
                    searchParams.forEach((value, key) => {
                      try {
                        if (value.startsWith('[') || value.startsWith('{')) {
                          criteria[key] = JSON.parse(value);
                        } else if (value === 'true' || value === 'false') {
                          criteria[key] = value === 'true';
                        } else {
                          criteria[key] = value;
                        }
                      } catch {
                        criteria[key] = value;
                      }
                    });
                    navigate('/employer/dashboard/zoeken/uitgebreid', { state: { criteria, returnToSearch: true } });
                  }}
                  className="w-full"
                >
                  {t('searchResults.adjustCriteria')}
                </Button>
              </div>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-4 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t('searchResults.title')}</h1>
          <p className="mt-1 text-gray-600">
            {professionals.length < totalCount
              ? t('searchResults.loadedOf', { loaded: professionals.length, total: totalCount })
              : t('searchResults.found', { count: totalCount })}
          </p>
        </div>

        <div className="flex items-center gap-3">
          {/* Sort dropdown */}
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600 whitespace-nowrap">{t('searchResults.sortBy')}:</label>
            <select
              value={currentSort}
              onChange={(e) => handleSortChange(e.target.value)}
              className="text-sm border border-gray-300 rounded-md px-3 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {SORT_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          <Button variant="outline" onClick={() => navigate('/employer/dashboard/zoeken/uitgebreid')}>
            {t('searchResults.newSearch')}
          </Button>
        </div>
      </div>

      {/* Active filter chips */}
      {activeFilters.length > 0 && (
        <div className="mb-5 flex flex-wrap items-center gap-2">
          <span className="text-sm text-gray-500">{t('searchResults.filters')}:</span>
          {activeFilters.map((chip, i) => (
            <span
              key={`${chip.key}-${i}`}
              className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
            >
              {chip.label}
            </span>
          ))}
          <button
            onClick={() => {
              // Build criteria object from current search params to carry back
              const criteria: any = {};
              searchParams.forEach((value, key) => {
                try {
                  if (value.startsWith('[') || value.startsWith('{')) {
                    criteria[key] = JSON.parse(value);
                  } else if (value === 'true' || value === 'false') {
                    criteria[key] = value === 'true';
                  } else {
                    criteria[key] = value;
                  }
                } catch {
                  criteria[key] = value;
                }
              });
              navigate('/employer/dashboard/zoeken/uitgebreid', { state: { criteria, returnToSearch: true } });
            }}
            className="text-xs text-blue-600 hover:text-blue-800 underline ml-1"
          >
            {t('searchResults.modify')}
          </button>
        </div>
      )}

      {/* "Laat Belagom voor u zoeken" banner */}
      {professionals.length > 0 && (
        <div className="mb-6">
          <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200">
            <div className="text-center py-4">
              <p className="text-gray-700 mb-3 font-medium">
                {t('searchResults.notFound')}
              </p>
              <Button onClick={handleRequestSearch} size="sm">
                {t('searchResults.letBelagomSearch')}
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Results grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {professionals.map((professional, index) => {
          const medaille = isMedailleTier(professional.compliance_tier);
          // Overgang van profielen met naar profielen zonder medaille zichtbaar maken
          const eersteStandaard = !medaille && index > 0 && isMedailleTier(professionals[index - 1].compliance_tier);
          return (
          <React.Fragment key={`${professional.id}-${index}`}>
          {eersteStandaard && (
            <div className="lg:col-span-2 flex items-center gap-3 text-xs font-semibold uppercase tracking-wide text-gray-400">
              <span className="h-px flex-1 bg-gray-200" />
              {t('searchResults.otherProfessionals')}
              <span className="h-px flex-1 bg-gray-200" />
            </div>
          )}
          <Card className={`hover:shadow-lg transition-shadow ${medaille ? TIER_RING[professional.compliance_tier as keyof typeof TIER_RING] : ''}`}>
            <div className="flex gap-4">
              {/* Photo with Belagom O logo ring */}
              <div className="flex-shrink-0">
                <BelagomProfilePhoto
                  src={professional.photo_url}
                  alt={`${professional.first_name} ${professional.last_name}`}
                  size={88}
                />
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {professional.first_name} {professional.last_name}
                    </h3>
                    {/* Praktijknaam + logo horen bij de profielkaart (FO) */}
                    {professional.company_name && (
                      <p className="flex items-center gap-2 text-sm font-medium text-gray-800">
                        {professional.company_logo_url && (
                          <img
                            src={professional.company_logo_url.startsWith('/') ? professional.company_logo_url : `/uploads/${professional.company_logo_url}`}
                            alt=""
                            className="h-5 max-w-[60px] object-contain"
                          />
                        )}
                        {professional.company_name}
                      </p>
                    )}
                    {professional.professional_type && (
                      <p className="text-sm text-gray-600">
                        {PROFESSIONAL_TYPE_LABELS[professional.professional_type as keyof typeof PROFESSIONAL_TYPE_LABELS] || professional.professional_type.replace(/_/g, ' ')}
                      </p>
                    )}
                  </div>

                  <div className="flex items-center gap-2 flex-shrink-0">
                    {medaille && professional.compliance_indicatoren && (
                      <ComplianceTierMedaille tier={professional.compliance_tier!} indicatoren={professional.compliance_indicatoren} size={52} />
                    )}
                    {hasMatchScores && professional.match_score !== null && professional.match_score !== undefined && (
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-bold ${
                        professional.match_score >= 80 ? 'bg-green-100 text-green-800' :
                        professional.match_score >= 60 ? 'bg-blue-100 text-blue-800' :
                        professional.match_score >= 40 ? 'bg-yellow-100 text-yellow-800' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {professional.match_score}% match
                      </span>
                    )}

                  {professional.average_rating && (
                    <div className="flex items-center flex-shrink-0">
                      <svg className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                      <span className="ml-1 text-sm font-medium text-gray-700">
                        {typeof professional.average_rating === 'number'
                          ? professional.average_rating.toFixed(1)
                          : parseFloat(String(professional.average_rating || 0)).toFixed(1)}
                      </span>
                      <span className="ml-1 text-xs text-gray-500">
                        ({professional.total_reviews || 0})
                      </span>
                    </div>
                  )}
                  </div>
                </div>

                {/* Introduction excerpt */}
                {professional.introduction && (
                  <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                    {professional.introduction}
                  </p>
                )}

                {/* Details */}
                <div className="flex flex-wrap gap-x-4 gap-y-2 text-sm text-gray-600 mb-3">
                  {professional.availability_status && (
                    <div className="flex items-center">
                      <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      {professional.availability_status === 'immediately' ? t('searchResults.availableNow') : t('searchResults.available')}
                    </div>
                  )}

                  {professional.distance_km != null && (
                    <div className="flex items-center">
                      <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      {professional.distance_km} km
                      {professional.travel_zone != null && (
                        <span className="ml-1 text-gray-500">· {t('searchResults.travelZone', { zone: professional.travel_zone })}</span>
                      )}
                    </div>
                  )}
                </div>

                {/* Buiten eigen werkgebied → bijbetaling reistijd voor opdrachtgever */}
                {professional.buiten_reisradius && (
                  <div className="mb-3 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
                    <span className="font-semibold">{t('searchResults.outsideRadius')}</span>{' '}
                    {professional.reistijd_bijbetaling_eur != null
                      ? t('searchResults.outsideRadiusFee', { amount: professional.reistijd_bijbetaling_eur.toFixed(2) })
                      : t('searchResults.outsideRadiusNoFee')}
                  </div>
                )}

                {/* Tags */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {(professional.specialisatie_tags || []).slice(0, 3).map((tag) => (
                    <span key={tag} className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded">
                      {taxonomyLabel(taxonomy?.specialisatie_tags, tag)}
                    </span>
                  ))}

                  {/* Werkgebied (dynamische reisradius i.p.v. provincies) */}
                  <span className={`px-2 py-1 text-xs rounded ${
                    professional.buiten_reisradius ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-700'
                  }`}>
                    {professional.reisradius_km != null
                      ? t('searchResults.workArea', { km: professional.reisradius_km })
                      : t('searchResults.workAreaWholeNl')}
                  </span>
                </div>

                {/* Actions */}
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={() => navigate(`/employer/professional/${professional.user_id}`)}
                  >
                    {t('searchResults.viewProfile')}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleContactProfessional(professional.user_id)}
                  >
                    {t('searchResults.contact')}
                  </Button>
                </div>
              </div>
            </div>
          </Card>
          </React.Fragment>
          );
        })}
      </div>

      {/* Infinite scroll trigger */}
      {hasMore && (
        <div ref={loadMoreRef} className="mt-8 text-center">
          {loadingMore && (
            <div className="py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-2"></div>
              <p className="text-gray-600 text-sm">{t('searchResults.loadingMore')}</p>
            </div>
          )}
        </div>
      )}

      {/* End of results message */}
      {!hasMore && professionals.length > 0 && (
        <div className="mt-8">
          <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200">
            <div className="text-center py-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {t('searchResults.noSuitableFound')}
              </h3>
              <p className="text-gray-700 mb-4 max-w-2xl mx-auto">
                {t('searchResults.noResultsDesc')}
              </p>
              <Button onClick={handleRequestSearch}>
                {t('searchResults.postSearch')}
              </Button>
            </div>
          </Card>
          <div className="mt-4 text-center">
            <p className="text-gray-500 text-sm">
              {t('searchResults.allLoaded', { count: totalCount })}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};
